import { useCallback, useEffect, useState } from "react";
import { getLanguageCodeForRegion, translationsService } from "@/api/translations.service";
import type { Submission } from "@/api/submissions.service";

export type TranslatableEntity = {
  submissionId?: string;
  id?: string;
  regionId?: number | null;
  practiceGroupId?: number | null;
  practiceAreaId?: number | null;
  tableId?: number | null;
  regionName?: string | null;
  practiceGroupName?: string | null;
  practiceAreaName?: string | null;
  tableName?: string | null;
};

export type WithTranslations = {
  regionTranslated?: string | null;
  practiceGroupTranslated?: string | null;
  practiceAreaTranslated?: string | null;
  tableTranslated?: string | null;
  languageCode?: string;
};

export type AddTranslations<T> = T & WithTranslations;

export type SubmissionWithTranslations = AddTranslations<Submission>;

type UseTranslationsOptions<T extends TranslatableEntity> = {
  entities: T[];
  enabled: boolean;
  getEntityId: (entity: T) => string | undefined;
};

type UseTranslationsResult<T> = {
  data: AddTranslations<T>[];
  loading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch translations for entities (submissions, referees, etc.) based on region
 */
export function useTranslations<T extends TranslatableEntity>({
  entities,
  enabled,
  getEntityId,
}: UseTranslationsOptions<T>): UseTranslationsResult<T> {
  const [data, setData] = useState<AddTranslations<T>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTranslations = useCallback(async () => {
    if (!enabled || entities.length === 0) {
      setData(entities);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Group entities by region to determine language codes
      const entitiesByLanguage = new Map<string, T[]>();
      const entitiesWithoutTranslations: T[] = [];

      entities.forEach(entity => {
        const languageCode = getLanguageCodeForRegion(entity.regionName ?? undefined);

        if (!languageCode) {
          entitiesWithoutTranslations.push(entity);
          return;
        }

        const existing = entitiesByLanguage.get(languageCode) || [];
        entitiesByLanguage.set(languageCode, [...existing, entity]);
      });

      // Fetch translations for each language group
      const translationPromises = Array.from(entitiesByLanguage.entries()).map(
        async ([languageCode, groupEntities]) => {
          // Collect all unique term IDs that need translation
          const termIds = new Set<string>();

          groupEntities.forEach(entity => {
            if (entity.regionId) termIds.add(String(entity.regionId));
            if (entity.practiceGroupId) termIds.add(String(entity.practiceGroupId));
            if (entity.practiceAreaId) termIds.add(String(entity.practiceAreaId));
            if (entity.tableId) termIds.add(String(entity.tableId));
          });

          // Fetch translations
          const translationMap = await translationsService.batchQuery(
            languageCode,
            Array.from(termIds),
          );

          // Apply translations to entities
          return groupEntities.map((entity): AddTranslations<T> => {
            return {
              ...entity,
              languageCode,
              regionTranslated: entity.regionId
                ? translationMap.get(String(entity.regionId)) || entity.regionName
                : undefined,
              practiceGroupTranslated: entity.practiceGroupId
                ? translationMap.get(String(entity.practiceGroupId)) || entity.practiceGroupName
                : undefined,
              practiceAreaTranslated: entity.practiceAreaId
                ? translationMap.get(String(entity.practiceAreaId)) || entity.practiceAreaName
                : undefined,
              tableTranslated: entity.tableId
                ? translationMap.get(String(entity.tableId)) || entity.tableName
                : undefined,
            };
          });
        },
      );

      const translatedGroups = await Promise.all(translationPromises);
      const allTranslated = translatedGroups.flat();

      // Combine translated and non-translated entities, preserving order
      // Use getEntityId to get unique key for each entity
      const resultMap = new Map<string, AddTranslations<T>>();

      allTranslated.forEach(translated => {
        const entityId = getEntityId(translated);
        if (entityId) {
          resultMap.set(entityId, translated);
        }
      });

      entitiesWithoutTranslations.forEach(entity => {
        const entityId = getEntityId(entity);
        if (entityId) {
          resultMap.set(entityId, entity);
        }
      });

      const orderedResult = entities.map(entity => {
        const entityId = getEntityId(entity);
        return entityId ? resultMap.get(entityId) || entity : entity;
      });

      setData(orderedResult);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch translations"));
      setData(entities);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, enabled]);
  // getEntityId is intentionally excluded - it's only used for result mapping,
  // not for fetching logic, and including it causes infinite loops

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  return { data, loading, error };
}
